<template>
  <div class="coupon-page">
    <TopPanel>优惠券</TopPanel>

    <div class="toolbar">
      <ul class="type-tabs">
        <li
          v-for="(tab, index) in typeTabs"
          :key="tab.valueKey"
          class="type-tab"
          :class="{ 'type-tab--active': activeTypeIndex === index }"
          @click="selectTypeTab(index)"
        >
          {{ tab.name }}
        </li>
      </ul>
      <a
        href="javascript:void(0)"
        class="instruction-link"
        @click="onInstruction"
      >
        使用说明
      </a>
    </div>

    <div class="list">
      <div
        v-for="item in coupons"
        :key="item.couponNo"
        class="coupon-card"
        :class="cardModifiers(item)"
      >
        <div class="coupon-card__left">
          <div class="coupon-card__amount">
            <template v-if="item.amountIsCash">
              <span class="coupon-card__currency">¥</span>
              <span class="coupon-card__num">{{ item.amountText }}</span>
            </template>
            <span v-else class="coupon-card__num coupon-card__num--discount">{{
              item.amountText
            }}</span>
          </div>
          <div class="coupon-card__rule">{{ item.thresholdText }}</div>
        </div>

        <div class="coupon-card__gap" aria-hidden="true"></div>

        <div class="coupon-card__mid">
          <span class="coupon-card__name">{{ item.name }}</span>
        </div>
        <div class="coupon-card__period">
          {{ item.validity }}
        </div>

        <div class="coupon-card__right">
          <button
            v-if="item.status === 'active'"
            type="button"
            class="btn-use"
            @click="onUse(item)"
          >
            立即使用
          </button>
          <span v-else-if="item.status === 'mobile_only'" class="hint-mobile">
            请至手机端使用
          </span>
        </div>

        <img
          v-if="item.status === 'used'"
          :src="couponStampUsed"
          class="coupon-card__stamp-img coupon-card__stamp-img--used"
          alt="已使用"
        />
        <img
          v-else-if="item.status === 'expired'"
          :src="couponStampExpired"
          class="coupon-card__stamp-img coupon-card__stamp-img--expired"
          alt="已过期"
        />
      </div>

      <div v-if="coupons.length === 0 && !listLoading" class="list-empty">
        暂无优惠券
      </div>
    </div>

    <div v-show="!pageEnd && coupons.length > 0" class="more" @click="loadMore">
      {{ listLoading ? '加载中…' : '点击查看更多' }}
    </div>

    <transition name="instruction-fade">
      <div
        v-if="instructionVisible"
        class="instruction-mask"
        @click.self="closeInstruction"
      >
        <div
          class="instruction-dialog"
          aria-labelledby="instruction-dialog-title"
        >
          <div class="instruction-dialog__header">
            <h2 id="instruction-dialog-title" class="instruction-dialog__title">
              优惠券使用说明
            </h2>
            <button
              type="button"
              class="instruction-dialog__close"
              aria-label="关闭"
              @click="closeInstruction"
            >
              ×
            </button>
          </div>
          <div class="instruction-dialog__body">
            <p class="instruction-dialog__para">
              1、此优惠券仅限线上使用，购买产品时（除特例产品外)，优惠券可抵券面显示的现金价值；
            </p>
            <p class="instruction-dialog__para">
              2、原则上每张订单只能使用一张优惠券，且不得与其他优惠方式同时使用；
            </p>
            <p class="instruction-dialog__para">
              3、此优惠券仅针对安卓客户端、微信公众号和PC版用户，ios客户端暂不支持使用；
            </p>
            <p class="instruction-dialog__para">
              4、此优惠券不兑付现金、不找零、不打包、不含税、不退款、过期未使用即失效不做补发。
            </p>
            <p class="instruction-dialog__para">
              优惠券使用说明解释权由第一财经有看投项目组所有。
            </p>
          </div>
          <div class="instruction-dialog__btn" @click="closeInstruction">
            知道了
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import TopPanel from '@/components/Mine/TopPanel'
import couponStampUsed from '@/assets/images/coupon/coupon-used.png'
import couponStampExpired from '@/assets/images/coupon/coupon-expired.png'
import {
  getCouponPage,
  CouponType,
  CouponScopeGoodsType,
  UserCouponStatus
} from '@/api/coupon'
import { getGoodsProducts } from '@/api/order'

function pick(row, camel, pascal) {
  const a = row[camel]
  return a !== undefined && a !== null ? a : row[pascal]
}

/** 后端 DateTimeOffset 序列化为毫秒时间戳 */
function toMs(v) {
  if (v == null) return null
  if (typeof v === 'number') return v
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function formatValidity(validFrom, validEnd) {
  const a = toMs(validFrom)
  const b = toMs(validEnd)
  if (!a || !b) return ''
  return `${dayjs(a).format('YYYY.MM.DD')}~${dayjs(b).format('YYYY.MM.DD')}`
}

function formatThreshold(minLimitAmount) {
  const n = Number(minLimitAmount)
  if (!Number.isFinite(n) || n <= 0) return '无门槛'
  return `满${stripTrailingZeros(n)}元可用`
}

function stripTrailingZeros(num) {
  const s = String(num)
  if (!s.includes('.')) return s
  return s.replace(/\.?0+$/, '')
}

/** 品类券 scopeValue → 站内列表首页 path */
function pathForCouponScopeGoodsType(scopeValue) {
  const n = Number(scopeValue)
  switch (n) {
    case CouponScopeGoodsType.JinNang:
      return '/jinNang'
    case CouponScopeGoodsType.Course:
      return '/course'
    case CouponScopeGoodsType.DianJin:
      return '/dianjin'
    default:
      return ''
  }
}

function formatDiscountMain(discount) {
  const d = Number(discount)
  if (!Number.isFinite(d) || d <= 0) return '--'
  if (d > 0 && d <= 1) {
    const zhe = d * 10
    return `${stripTrailingZeros(zhe)}折`
  }
  return `${stripTrailingZeros(d)}折`
}

/**
 * @param {*} row UserCouponApiModel（含 PascalCase 容错）
 */
function mapUserCouponRow(row) {
  const couponNo = pick(row, 'couponNo', 'CouponNo') ?? ''
  const name = pick(row, 'name', 'Name') ?? ''
  const statusRaw = pick(row, 'status', 'Status')
  const valueAmount = pick(row, 'valueAmount', 'ValueAmount')
  const discount = pick(row, 'discount', 'Discount')
  const minLimitAmount = pick(row, 'minLimitAmount', 'MinLimitAmount')
  const validFrom = pick(row, 'validFrom', 'ValidFrom')
  const validEnd = pick(row, 'validEnd', 'ValidEnd')
  const appLinkUrl = pick(row, 'appLinkUrl', 'AppLinkUrl')
  const kind = pick(row, 'kind', 'Kind')
  const typeRaw = pick(row, 'type', 'Type')
  const couponType = Number(typeRaw)
  const scopeRaw = pick(row, 'scopeValue', 'ScopeValue')
  const scopeValue = Number(scopeRaw)

  const hasCash =
    valueAmount != null &&
    valueAmount !== '' &&
    Number.isFinite(Number(valueAmount))
  const amountIsCash = hasCash
  const amountText = amountIsCash
    ? stripTrailingZeros(Number(valueAmount))
    : formatDiscountMain(discount)

  let uiStatus = 'active'
  const s = Number(statusRaw)
  if (s === UserCouponStatus.Used) {
    uiStatus = 'used'
  } else if (s === UserCouponStatus.Locked) {
    uiStatus = 'locked'
  } else if (s === UserCouponStatus.Expired) {
    uiStatus = 'expired'
  } else if (s === UserCouponStatus.Recycled) {
    uiStatus = 'recycled'
  }

  const pcVlaidScopeValue = [
    CouponScopeGoodsType.JinNang,
    CouponScopeGoodsType.Course,
    CouponScopeGoodsType.DianJin
  ]
  if (
    uiStatus === 'active' &&
    (kind !== 0 ||
      (couponType === CouponType.SpecificGoodsType &&
        !pcVlaidScopeValue.includes(scopeValue)))
  ) {
    uiStatus = 'mobile_only'
  }

  return {
    couponNo,
    name,
    amountText,
    amountIsCash,
    thresholdText: formatThreshold(minLimitAmount),
    validity: formatValidity(validFrom, validEnd),
    status: uiStatus,
    appLinkUrl: appLinkUrl || '',
    couponType: Number.isFinite(couponType) ? couponType : NaN,
    scopeValue: Number.isFinite(scopeValue) ? scopeValue : NaN
  }
}

export default {
  name: 'MyCoupon',
  components: { TopPanel },
  data() {
    return {
      activeTypeIndex: 0,
      typeTabs: [
        { name: '全部', valueKey: 'all', couponType: null },
        {
          name: '全场券',
          valueKey: 'universal',
          couponType: CouponType.Universal
        },
        {
          name: '品类券',
          valueKey: 'category',
          couponType: CouponType.SpecificGoodsType
        },
        {
          name: '单品券',
          valueKey: 'single',
          couponType: CouponType.SpecificGoodsId
        }
      ],
      coupons: [],
      viewState: '',
      pageEnd: false,
      listLoading: false,
      lastUseCouponId: null,
      couponStampUsed,
      couponStampExpired,
      instructionVisible: false
    }
  },
  created() {
    this.reloadList()
  },
  methods: {
    selectTypeTab(index) {
      if (this.activeTypeIndex === index) return
      this.activeTypeIndex = index
      this.reloadList()
    },
    pageRequestFilter() {
      const tab = this.typeTabs[this.activeTypeIndex]
      if (!tab || tab.couponType == null) {
        return {}
      }
      return { type: tab.couponType }
    },
    async reloadList() {
      this.viewState = ''
      this.pageEnd = false
      this.coupons = []
      await this.fetchPage()
    },
    async loadMore() {
      if (this.pageEnd || this.listLoading) return
      await this.fetchPage()
    },
    async fetchPage() {
      if (this.listLoading) return
      this.listLoading = true
      try {
        const data = await getCouponPage(
          this.pageRequestFilter(),
          this.viewState
        )
        if (!data || data.result?.code !== 0) {
          return
        }
        this.viewState = data.result.viewState
        const detail = data.detail || {}
        this.pageEnd = !!detail.end
        const rawList = detail.list || []
        const mapped = rawList.map(mapUserCouponRow)

        await this.resolveGoodsCouponStatus(mapped)

        if (detail.flush) {
          this.coupons = mapped
        } else {
          this.coupons = this.coupons.concat(mapped)
        }
        if (rawList.length === 0) {
          this.pageEnd = true
        }
      } finally {
        this.listLoading = false
      }
    },
    cardModifiers(item) {
      const d =
        item.status === 'used' ||
        item.status === 'expired' ||
        item.status === 'locked' ||
        item.status === 'recycled'
      return {
        'coupon-card--disabled': d
      }
    },
    onInstruction() {
      this.instructionVisible = true
    },
    closeInstruction() {
      this.instructionVisible = false
    },
    onUse(item) {
      this.lastUseCouponId = item.couponNo
      let target = '/'
      if (item.couponType === CouponType.SpecificGoodsType) {
        const p = pathForCouponScopeGoodsType(item.scopeValue)
        if (p) target = p
      } else if (item.couponType === CouponType.SpecificGoodsId) {
        const tt = item._goodsTargetType
        if (tt === 1) {
          target = `/jinNang/${item._goodsTargetId}`
        } else if (tt === 2) {
          target = `/course/${item._goodsTargetId}`
        } else if (tt === 3) {
          target = `/author/${item._goodsAuthorId}`
        }
      }
      this.$router.push(target).catch(() => {})
    },
    async resolveGoodsCouponStatus(mappedList) {
      const goodsCoupons = mappedList.filter(
        item =>
          item.status === 'active' &&
          item.couponType === CouponType.SpecificGoodsId
      )
      if (goodsCoupons.length === 0) return

      const goodsIds = goodsCoupons
        .map(c => c.scopeValue)
        .filter(id => Number.isFinite(id))
      if (goodsIds.length === 0) return

      const products = await getGoodsProducts(goodsIds)
      if (!products || products.length === 0) return

      const validTargetTypes = new Set([1, 2, 3])
      const goodsProductMap = new Map()
      for (const p of products) {
        goodsProductMap.set(p.goodsId, p)
      }

      for (const coupon of goodsCoupons) {
        const product = goodsProductMap.get(coupon.scopeValue)
        if (!product || !validTargetTypes.has(product.targetType)) {
          coupon.status = 'mobile_only'
          continue
        }
        coupon._goodsTargetType = product.targetType
        coupon._goodsTargetId = product.targetId
        coupon._goodsAuthorId = product.authorId
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$blue: #046bcc;
$orange: #e64c2e;
$bg-active: #fff5f2;
$bg-disabled: #f5f5f5;

.coupon-page {
  width: 828px;
  background-color: #ffffff;
  border: solid 1px #ebebeb;
  margin-left: 30px;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
  box-sizing: border-box;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 30px 0px;
}

.type-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.type-tab {
  min-width: 72px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  font-size: 14px;
  color: #595959;
  background: #f0f0f0;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.type-tab--active {
  color: $blue;
  background: rgba($blue, 0.1);
}

.instruction-link {
  flex-shrink: 0;
  margin-left: 16px;
  font-size: 14px;
  color: $blue;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.list {
  padding: 30px 30px 0;
}

.coupon-card {
  position: relative;
  display: flex;
  align-items: stretch;
  height: 80px;
  margin-bottom: 24px;
  background: $bg-active;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
}

.coupon-card--disabled {
  background: $bg-disabled;

  .coupon-card__currency,
  .coupon-card__num,
  .coupon-card__rule,
  .coupon-card__name,
  .coupon-card__period {
    color: #8c8c8c;
  }

  .coupon-card__amount {
    opacity: 0.85;
    font-family: PingFang SC, PingFang SC-Regular;
  }
}

.coupon-card__left {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.coupon-card__amount {
  line-height: 30px;
  height: 30px;
  color: $orange;
}

.coupon-card__currency {
  font-size: 18px;
  font-weight: 600;
  margin-right: 2px;
}

.coupon-card__num {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -1px;
}

.coupon-card__num--discount {
  font-size: 28px;
}

.coupon-card__rule {
  margin-top: 11px;
  font-size: 16px;
  height: 15px;
  font-family: PingFang SC, PingFang SC-Regular;
  color: #858585;
  line-height: 15px;
}

.coupon-card--disabled .coupon-card__rule {
  color: #8c8c8c;
}

.coupon-card__gap {
  background-image: url('~@/assets/images/coupon/devider.png');
  justify-content: center;
  align-items: center;
  height: 80px;
  width: 16px;
}

.coupon-card__gap-line {
  width: 0;
  height: 72%;
  border-left: 1px dashed rgba(0, 0, 0, 0.12);
}

.coupon-card--disabled .coupon-card__gap-line {
  border-left-color: rgba(0, 0, 0, 0.08);
}

.coupon-card__mid {
  display: flex;
  align-items: center;
  padding-left: 40px;
  min-width: 0;
}

.coupon-card__title-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.coupon-card__name {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.coupon-card__period {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 35px;
  color: #858585;
  font-size: 16px;
  font-family: PingFang SC, PingFang SC-Regular;
  font-weight: 400;
}

.coupon-card__right {
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 30px;
}

.btn-use {
  padding: 0 18px;
  height: 30px;
  line-height: 30px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  color: #fff;
  background: $orange;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    filter: brightness(1.05);
  }
}

.coupon-card--disabled .btn-use {
  display: none;
}

.hint-mobile {
  font-size: 13px;
  color: #e64c2e;
  text-align: right;
  line-height: 1.4;
  max-width: 96px;
}

.coupon-card--disabled .hint-mobile {
  color: #8c8c8c;
}

.coupon-card__stamp {
  position: absolute;
  top: 12px;
  right: 16px;
  padding: 4px 10px;
  font-size: 12px;
  color: #666666;
  border: 1px dashed #bfbfbf;
  border-radius: 2px;
  transform: rotate(-18deg);
  pointer-events: none;
}

.coupon-card__stamp-img {
  position: absolute;
  top: 0px;
  right: 0px;
  max-width: 72px;
  height: auto;
  pointer-events: none;
  display: block;
}

.list-empty {
  padding: 48px 0;
  text-align: center;
  font-size: 14px;
  color: #8c8c8c;
}

.more {
  width: 100%;
  height: 40px;
  margin: 30px auto 0;
  font-size: 14px;
  line-height: 40px;
  color: #595959;
  background-color: #fafafa;
  text-align: center;
  cursor: pointer;
}

.instruction-mask {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.45);
}

.instruction-dialog {
  width: 100%;
  max-width: 700px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 28px 28px 49px;
  box-sizing: border-box;
  position: relative;
}

.instruction-dialog__header {
  position: relative;
  padding-right: 36px;
  margin-bottom: 22px;
}

.instruction-dialog__title {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  color: #333333;
  line-height: 1.3;
  letter-spacing: 0.02em;
}

.instruction-dialog__close {
  position: absolute;
  top: -4px;
  right: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: #bfbfbf;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #999999;
  }
}

.instruction-dialog__body {
  margin: 0;
}

.instruction-dialog__para {
  margin: 0 0 14px;
  font-size: 16px;
  line-height: 24px;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
}

.instruction-dialog__btn {
  width: 160px;
  height: 46px;
  line-height: 46px;
  margin: 30px auto 0;
  box-sizing: border-box;
  border: none;
  border-radius: 6px;
  background-color: #2b65c5;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  text-align: center;

  &:hover {
    filter: brightness(1.05);
  }
}

.instruction-fade-enter-active,
.instruction-fade-leave-active {
  transition: opacity 0.2s ease;
}

.instruction-fade-enter-active .instruction-dialog,
.instruction-fade-leave-active .instruction-dialog {
  transition: transform 0.2s ease;
}

.instruction-fade-enter,
.instruction-fade-leave-to {
  opacity: 0;

  .instruction-dialog {
    transform: scale(0.96);
  }
}
</style>
